import { useCallback, useMemo, useState } from 'react';
import { pageErrorContext } from './pageErrorContext';

export interface Props {
  children?: React.ReactNode;
}

export const PageErrorProvider = ({ children }: Props) => {
  const [errors, setErrors] = useState<Record<string, boolean>>({});
  const [errorMessages, setErrorMessages] = useState<Record<string, string>>({});

  const setError = useCallback((key: string, hasError: boolean, errorMessage?: string) => {
    setErrors((prev) => ({
      ...prev,
      [key]: hasError,
    }));

    if (hasError && errorMessage) {
      setErrorMessages((prev) => ({
        ...prev,
        [key]: errorMessage,
      }));
    } else if (!hasError) {
      setErrorMessages((prev) => {
        const newMessages = { ...prev };
        delete newMessages[key];
        return newMessages;
      });
    }
  }, []);

  const clearError = useCallback((key: string) => {
    setErrors((prev) => {
      const newErrors = { ...prev };
      delete newErrors[key];
      return newErrors;
    });

    setErrorMessages((prev) => {
      const newMessages = { ...prev };
      delete newMessages[key];
      return newMessages;
    });
  }, []);

  const hasError = Object.values(errors).some((error) => error);
  const errorMessage = Object.values(errorMessages)[Object.values(errorMessages).length - 1];

  const contextValue = useMemo(
    () => ({
      hasError,
      errorMessage,
      errors,
      setError,
      clearError,
    }),
    [hasError, errorMessage, errors, setError, clearError],
  );

  return <pageErrorContext.Provider value={contextValue}>{children}</pageErrorContext.Provider>;
};
